import { generateSurveySectionGuidance } from '../businessPlanSectionGenerator';
import { BusinessPlanSurveySectionGuidanceKey } from '../types';
import { useFetchedPromptVariables } from '../../growegy-ai/variables/hooks/useFetchedPromptVariables';
import {
  PromptVariableListValue,
  PromptVariableStringValue,
  VARIABLE_AUDIENCE,
  VARIABLE_COMPANY,
  VARIABLE_FUNDING_NEED,
  VARIABLE_INCORPORATION_STATE,
  VARIABLE_INCORPORATION_TYPE,
  VARIABLE_PERSONNEL,
  VARIABLE_PRODUCT_OR_SERVICE,
} from '../../growegy-ai/types';
import { useCallback } from 'react';
import { useSurveyWithLocalChanges } from './useSurveyWithLocalChanges';
import { useFetchedBillingCustomer } from '../../billing/hooks/useFetchedBillingCustomer';
import { variableValueToContent } from '../../growegy-ai/util';

export const useBusinessPlanMessageGenerator = () => {
  const {
    query: { data: variables },
  } = useFetchedPromptVariables();
  const {
    query: { data: customer },
  } = useFetchedBillingCustomer();
  const companyVariable = variables?.find((v) => v.variableValue.name === VARIABLE_COMPANY)
    ?.variableValue as PromptVariableStringValue | undefined;
  const companyName = customer ? customer.name : companyVariable?.value ?? '';
  const companyDescription = companyVariable?.description ?? '';
  const productOrServiceVariable = variables?.find(
    (v) => v.variableValue.name === VARIABLE_PRODUCT_OR_SERVICE
  )?.variableValue as PromptVariableListValue | undefined;
  const audienceVariable = variables?.find((v) => v.variableValue.name === VARIABLE_AUDIENCE)
    ?.variableValue as PromptVariableListValue | undefined;
  const incorporationTypeVariable = variables?.find(
    (v) => v.variableValue.name === VARIABLE_INCORPORATION_TYPE
  )?.variableValue as PromptVariableStringValue | undefined;
  const incorporationStateVariable = variables?.find(
    (v) => v.variableValue.name === VARIABLE_INCORPORATION_STATE
  )?.variableValue as PromptVariableStringValue | undefined;
  const fundingNeedVariable = variables?.find((v) => v.variableValue.name === VARIABLE_FUNDING_NEED)
    ?.variableValue as PromptVariableStringValue | undefined;
  const personnelVariable = variables?.find((v) => v.variableValue.name === VARIABLE_PERSONNEL)
    ?.variableValue as PromptVariableListValue | undefined;

  const {
    query: { data: surveyWithLocalChanges },
  } = useSurveyWithLocalChanges();

  return useCallback(
    (sectionDraft: string, surveySectionKey: BusinessPlanSurveySectionGuidanceKey) => {
      return generateSurveySectionGuidance(
        surveySectionKey,
        {
          companyName,
          companyDescription,
          productOrService:
            productOrServiceVariable?.values?.map((x) => variableValueToContent(x)) ?? [],
          targetAudience: audienceVariable?.values?.map((x) => variableValueToContent(x)) ?? [],
          incorporationType: incorporationTypeVariable
            ? variableValueToContent(incorporationTypeVariable)
            : undefined,
          incorporationState: incorporationStateVariable
            ? variableValueToContent(incorporationStateVariable)
            : undefined,
          fundingNeed: fundingNeedVariable
            ? variableValueToContent(fundingNeedVariable)
            : undefined,
          principalMembers: personnelVariable?.values?.map((x) => variableValueToContent(x)) ?? [],
        },
        sectionDraft,
        surveyWithLocalChanges
      );
    },
    [
      surveyWithLocalChanges,
      companyName,
      companyDescription,
      audienceVariable?.values,
      productOrServiceVariable?.values,
      incorporationTypeVariable,
      incorporationStateVariable,
      fundingNeedVariable,
      personnelVariable,
    ]
  );
};
